import { useEffect, useMemo, useState } from "react";

import { PalButton, PalDialog } from "@palamar/fe-library";
import { withCardon } from "cardon";
import { useTranslation } from "react-i18next";
import { MdOutlineCloud } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";

import { Alert, Grid } from "@mui/material";

import PRContainer from "~components/Generic/PRContainer";
import { eCommerceDataSourceStatusType, eCommerceDefinitionList, eCommerceProject } from "~constants";
import AlertHelper from "~helpers/AlertHelper";
import { IntegrationDetailModal, ManualIntegrationModal } from "~pages/Onboarding/CommonComponents";
import { ECommerceItem } from "~pages/Onboarding/steps/StepECommerce";
import { fetchDirectoryList } from "~store/productQA/actions";
import {
  createOrUpdateECommerceIntegration,
  getECommerceIntegrationDataSourceList,
  getECommerceList,
  runIntegrationConnectionTests,
  testECommerceIntegration,
} from "~store/settings/eCommerce/actions";
import { selectECommerceDataSourceList, selectECommerceList } from "~store/settings/eCommerce/selectors";
import { selectCurrentProject } from "~store/user/selectors";

const RemainingProductModal = withCardon(
  function RemainingProductModalContent({ get, list }) {
    let items = list;
    const handleCancel = () => {
      get(false)();
    };
    const { t } = useTranslation();
    const handleClose = () => {
      get(false)();
    };
    const filterPendingProducts = useMemo(() => {
      return items.filter((item) => item.remaining_new_products > 0);
    }, [items]);
    return (
      <PalDialog fullWidth maxWidth="sm" open={open} scroll="body" onClose={handleClose}>
        <PalDialog.Title>{t("component.remainingProductModal.title")} </PalDialog.Title>
        <PalDialog.Content dividers={false}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", padding: "8px" }}>{t("component.remainingProductModal.name")}</th>
                <th style={{ textAlign: "left", padding: "8px" }}>{t("component.remainingProductModal.remaining")}</th>
              </tr>
            </thead>
            <tbody>
              {filterPendingProducts?.map((item) => (
                <tr key={item.id}>
                  <td style={{ padding: "8px" }}>{item.name}</td>
                  <td style={{ padding: "8px" }}>{item.remaining_new_products}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </PalDialog.Content>
        <PalDialog.Actions>
          <PalButton onClick={handleCancel}>{t("common.ok")}</PalButton>
        </PalDialog.Actions>
      </PalDialog>
    );
  },
  { destroyOnHide: true }
);

export default function ECommerce() {
  const { t } = useTranslation();
  const currentProject = useSelector(selectCurrentProject);
  const dispatch = useDispatch();
  const eCommerceList = useSelector(selectECommerceList);
  const eCommerceDataSourceList = useSelector(selectECommerceDataSourceList);
  const [projectDirectoryList, setProjectDirectoryList] = useState([]);

  useEffect(() => {
    if (currentProject?.id) {
      dispatch(getECommerceList(currentProject.id));
      dispatch(getECommerceIntegrationDataSourceList(currentProject.id));
      dispatch(fetchDirectoryList(currentProject.id, "")).then((response) => {
        setProjectDirectoryList(response?.results || []);
      });
    }
  }, [dispatch, currentProject?.id]);

  const handleClickShowDetails = (integration) => () => {
    const eCommerceDefinition = eCommerceDefinitionList.find((item) => {
      //NOTE: (BUG) predefined key before comparison. This issues a bug for comparison and won't work as expected.
      const key = item.key;
      return key === integration.platform_type;
    });

    IntegrationDetailModal.show({
      integration: integration?.credentials,
      labelDefinition: eCommerceDefinition.configParams.reduce((acc, item) => {
        acc[item.key] = item.label;
        return acc;
      }, {}),
    });
  };

  const handleManualIntegration = (integrationName) => async () => {
    const integration = eCommerceDefinitionList.find((item) => item.key === integrationName);
    const inputFields = integration.configParams.map((item) => {
      return {
        ...item,
        label: item.label,
        key: item.key,
        required: item.required,
      };
    });
    inputFields.push({
      label: t("common.active"),
      key: "is_active",
      required: false,
      type: "switch",
    });
    const integrationData = eCommerceList?.find((item) => item.platform_type === integrationName);
    const state = {
      ...integrationData?.credentials,
      is_active: integrationData?.is_active ?? true,
    };
    const responseState = await ManualIntegrationModal.show({
      integrationName: integration.name,
      inputFields,
      state,
      showDelete: true,
    });

    const isDeleteMode = Object.values(responseState).every((item) => !item);

    if (responseState) {
      const newState = {
        ...responseState,
      };
      if (newState?.access_token && newState?.access_token === state?.access_token) {
        delete newState.access_token;
      }
      if (newState?.refresh_token && newState?.refresh_token === state?.refresh_token) {
        delete newState.refresh_token;
      }
      if (newState?.client_secret && newState?.client_secret === state?.client_secret) {
        delete newState.client_secret;
      }
      if (newState?.password && newState?.password === state?.password) {
        delete newState.password;
      }
      if (newState?.member_code && newState?.member_code === state?.member_code) {
        delete newState.member_code;
      }
      const isActiveState = newState?.is_active;
      delete newState.is_active;
      //detect rest fields are changed or not
      let isRestFieldsChanged = false;

      for (const key in newState) {
        if (newState[key] !== state[key]) {
          isRestFieldsChanged = true;
          break;
        }
      }

      await dispatch(
        createOrUpdateECommerceIntegration(currentProject.id, {
          ...integrationData,
          credentials: isRestFieldsChanged ? newState : undefined, //set undefined to override the old credentials to none
          is_active: isDeleteMode ? false : isActiveState,
          platform_type: integrationName,
        })
      );
      await dispatch(getECommerceList(currentProject.id));
      await dispatch(getECommerceIntegrationDataSourceList(currentProject.id));
    }
  };

  const actions = useMemo(() => {
    return [
      {
        label: t("dashboard.eCommerce.syncIntegrations"),
        icon: MdOutlineCloud,
        color: "secondary",
        onClick: async () => {
          await dispatch(runIntegrationConnectionTests(currentProject.id));
          await dispatch(getECommerceIntegrationDataSourceList(currentProject.id));
        },
      },
    ];
  }, [t, dispatch, currentProject]);

  useEffect(() => {
    //make request every 10 secs when eCommerceDataSourceList's an item has any processing status
    const interval = setInterval(() => {
      if (eCommerceDataSourceList?.some((item) => item.status === eCommerceDataSourceStatusType.PROCESSING)) {
        dispatch(getECommerceIntegrationDataSourceList(currentProject.id));
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [dispatch, currentProject.id, eCommerceDataSourceList]);

  const hasAnyPendingProduct = useMemo(() => {
    return projectDirectoryList.some((item) => item.remaining_new_products > 0);
  }, [projectDirectoryList]);

  const handleClickOpenRemainingProductModal = async () => {
    const response = await dispatch(fetchDirectoryList(currentProject.id, ""));
    setProjectDirectoryList(response?.results || []);
    RemainingProductModal.show({ list: response?.results || [] });
  };
  return (
    <PRContainer
      actions={actions}
      className="pr-ecommerce"
      description={t("dashboard.eCommerce.description")}
      name={t("common.integration")}
      parentName={t("dashboard.eCommerce")}
    >
      {hasAnyPendingProduct && (
        <Alert className="mb-2" severity="info">
          {t("dashboard.eCommerce.pendingProducts")}
          <PalButton
            color="primary"
            sx={{
              p: "0px 8px",
            }}
            variant="text"
            onClick={handleClickOpenRemainingProductModal}
          >
            {t("common.viewDetails")}
          </PalButton>
        </Alert>
      )}
      <Grid container spacing={2}>
        {eCommerceDefinitionList.map((item) => {
          //NOTE: (BUG) predefined key before comparison. This issues a bug for comparison and won't work as expected.
          const key = item.key;
          const eCommerce = eCommerceList.find((item) => item.platform_type === key);
          const credentialsObj = eCommerce?.credentials;
          const isAllCredentialsFilled = item.configParams.every((param) => {
            return credentialsObj?.[param.key];
          });

          const relatedDataSourceList = eCommerceDataSourceList?.filter(
            (item) => item.platform_integration?.id === eCommerce?.id
          );

          const handleClickTest = async () => {
            const testResponse = await dispatch(testECommerceIntegration(currentProject.id, eCommerce.id));
            const isSuccess = testResponse?.status === true;
            if (isSuccess) {
              AlertHelper.showSuccess(t("common.integrationTest.success"));
            } else {
              AlertHelper.showWarning(t("common.integrationTest.fail"));
            }
          };
          const isShopifyAndConnected = item.key === eCommerceProject.PS && !!currentProject?.shopify_integration;
          const handleClickGoToShopifySetupPage = async () => {
            // const deepLinkResponse = await dispatch(getShopifyDeepLink(currentProject?.shopify_integration));
            // if (deepLinkResponse?.deep_link) {
            //   window.open(deepLinkResponse?.deep_link, "_blank");
            // }
            // http://localhost:3500/dashboard/setup/293/?shopifyConnDone=true&shopifyInt=true

            window.open(`/dashboard/setup/${currentProject?.id}/?shopifyConnDone=true&shopifyInt=true`, "_blank");
          };

          return (
            <Grid key={item.key} item md={4} sm={6} xl={3} xs={12}>
              <ECommerceItem
                // desc={`Connect your ${item.name} account to chat with your customers.`}
                dataSourceList={relatedDataSourceList}
                hideTestButton={isShopifyAndConnected}
                image={item.logo}
                imageStyles={item.logoStyles}
                isDisabled={isAllCredentialsFilled && eCommerce?.is_active === false}
                isDone={isAllCredentialsFilled}
                name={item.name}
                onClick={handleManualIntegration(item.key)}
                onClickDetail={handleClickShowDetails(eCommerce)}
                onClickTest={handleClickTest}
                {...((isAllCredentialsFilled || isShopifyAndConnected) && {
                  onClickConfigure: isShopifyAndConnected
                    ? handleClickGoToShopifySetupPage
                    : handleManualIntegration(item.key),
                })}
              />
            </Grid>
          );
        })}
      </Grid>
    </PRContainer>
  );
}
